import React from "react";
import { Customer } from "@/pages/dashboard/Customers";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Edit, Save, Mail, Phone, Clock, History } from "lucide-react";
import { format } from "date-fns";
import { ClientMessaging } from "./ClientMessaging";

interface CustomerDetailsProps {
  customer: Customer;
  isEditing: boolean;
  onEdit: () => void;
  onSave: (customer: Customer) => void;
  editedCustomer: Customer;
  onCustomerChange: (field: keyof Customer, value: string | boolean) => void;
}

export function CustomerDetails({
  customer,
  isEditing,
  onEdit,
  onSave,
  editedCustomer,
  onCustomerChange,
}: CustomerDetailsProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{customer.name}</CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={isEditing ? () => onSave(editedCustomer) : onEdit}
        >
          {isEditing ? (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save
            </>
          ) : (
            <>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </>
          )}
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Email</Label>
              {isEditing ? (
                <Input
                  value={editedCustomer.email}
                  onChange={(e) => onCustomerChange('email', e.target.value)}
                />
              ) : (
                <div className="flex items-center mt-1 text-sm text-muted-foreground">
                  <Mail className="h-4 w-4 mr-2" />
                  {customer.email}
                </div>
              )}
            </div>
            <div>
              <Label>Phone</Label>
              {isEditing ? (
                <Input
                  value={editedCustomer.phone}
                  onChange={(e) => onCustomerChange('phone', e.target.value)}
                />
              ) : (
                <div className="flex items-center mt-1 text-sm text-muted-foreground">
                  <Phone className="h-4 w-4 mr-2" />
                  {customer.phone}
                </div>
              )}
            </div>
          </div>
          <div className="pt-2">
            <Label>Consent</Label>
            {isEditing ? (
              <div className="mt-2 space-y-2 rounded-md border p-3">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="smsConsentDetails"
                    checked={!!editedCustomer.smsConsent}
                    onCheckedChange={(v) => onCustomerChange('smsConsent', !!v)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="smsConsentDetails" className="text-sm">Allow appointment SMS reminders</Label>
                    <p className="text-xs text-muted-foreground">Standard rates may apply. Opt out anytime.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="marketingConsentDetails"
                    checked={!!editedCustomer.marketingConsent}
                    onCheckedChange={(v) => onCustomerChange('marketingConsent', !!v)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="marketingConsentDetails" className="text-sm">Allow occasional marketing messages</Label>
                    <p className="text-xs text-muted-foreground">We’ll only send relevant offers. Opt out anytime.</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant={customer.smsConsent ? 'default' : 'secondary'} className="text-xs">
                    SMS {customer.smsConsent ? 'Allowed' : 'Blocked'}
                  </Badge>
                  <Badge variant={customer.marketingConsent ? 'default' : 'secondary'} className="text-xs">
                    Marketing {customer.marketingConsent ? 'Opt-in' : 'Opt-out'}
                  </Badge>
                </div>
                <div className="flex items-start gap-6">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="smsConsentQuick"
                      checked={!!customer.smsConsent}
                      onCheckedChange={(v) => onCustomerChange('smsConsent', !!v)}
                    />
                    <Label htmlFor="smsConsentQuick" className="text-sm">Allow appointment SMS reminders</Label>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="marketingConsentQuick"
                      checked={!!customer.marketingConsent}
                      onCheckedChange={(v) => onCustomerChange('marketingConsent', !!v)}
                    />
                    <Label htmlFor="marketingConsentQuick" className="text-sm">Allow marketing messages</Label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="pt-4 border-t space-y-4">
          <div>
            <div className="text-sm font-medium flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              Appointment History
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              {customer.appointmentCount} total appointments
            </div>
          </div>
          
          <div>
            <div className="text-sm font-medium flex items-center">
              <History className="h-4 w-4 mr-2" />
              Last Visit
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              {customer.lastVisit 
                ? format(customer.lastVisit, "MMMM d, yyyy") 
                : "No previous visits"}
            </div>
          </div>
        </div>

        <div className="pt-4 border-t">
          <Label>Notes</Label>
          {isEditing ? (
            <Textarea
              value={editedCustomer.notes}
              onChange={(e) => onCustomerChange('notes', e.target.value)}
              className="mt-2"
              placeholder="Add notes about this customer..."
            />
          ) : (
            <div className="mt-2 text-sm text-muted-foreground">
              {customer.notes || "No notes added"}
            </div>
          )}
        </div>

        {!isEditing && (
          <div className="pt-4 border-t">
            <ClientMessaging 
              clientId={customer.id}
              phone={customer.phone}
              lastVisit={customer.lastVisit}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
